import {
  DesktopDatePicker as DatePicker,
  DesktopTimePicker as TimePicker,
  DatePickerProps,
  TimePickerProps,
  DateTimePickerProps as MuiDateTimePickerProps,
  DesktopDateTimePicker as MuiDateTimePicker,
} from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { TextFieldProps as TFProps } from '@mui/material/TextField';
import TextField, { TextFieldLoader } from '../TextField/TextField';
import CalendarIcon from '../../assets/icons/CalendarIcon';
import ErrorIcon from '../../assets/icons/ErrorIcon';
import WarningIcon from '../../assets/icons/Warning';
import TooltipErrors from '../TooltipErrors/TooltipErrors';
import { ErrorMessages } from '../../utils/consts';
import { formatDateForDisplay } from '../../utils/dateFormat';
import {
  datePickerStyles,
  DEFAULT_MIN_DATE,
  DEFAULT_MAX_DATE,
  MIN_YEAR,
  MAX_YEAR,
  MIN_COMPLETE_YEAR,
} from './DateTimePickerProps';
import { Box, IconButton, SxProps } from '@mui/material';

type TextFieldProps = Pick<
  TFProps,
  | 'value'
  | 'defaultValue'
  | 'name'
  | 'className'
  | 'placeholder'
  | 'label'
  | 'required'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'size'
  | 'inputProps'
  | 'InputProps'
> & {
  endAdornmentDivider?: boolean;
  endAdornment?: React.ReactElement;
  tooltip?: React.ReactNode;
  warning?: boolean;
};

type BaseProps = {
  date?: boolean;
  time?: boolean;
  textFieldProps?: TextFieldProps;
  onAccept?: (value: dayjs.Dayjs | null) => void;
  onChange?: (value: dayjs.Dayjs | null) => void;
  onError?: (reason: string | null) => void;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  error?: boolean;
  warning?: boolean;
  tooltip?: React.ReactNode;
  required?: boolean;
  'data-testid'?: string;
  rootSx?: SxProps;
  loading?: boolean;
  disablePortal?: boolean;
  small?: boolean;
  ignoreIncompleteEntries?: boolean;
};
const MIN_DATE_LABEL = formatDateForDisplay(DEFAULT_MIN_DATE);
const MAX_DATE_LABEL = formatDateForDisplay(DEFAULT_MAX_DATE);

const withDateOf = (value: dayjs.Dayjs, boundary: dayjs.Dayjs) =>
  value.year(boundary.year()).month(boundary.month()).date(boundary.date());

const clampToRange = (value: dayjs.Dayjs) => {
  const withClampedYear = value.year(
    Math.min(Math.max(value.year(), MIN_YEAR), MAX_YEAR)
  );

  if (withClampedYear.isBefore(DEFAULT_MIN_DATE, 'day')) {
    return withDateOf(withClampedYear, DEFAULT_MIN_DATE);
  }
  if (withClampedYear.isAfter(DEFAULT_MAX_DATE, 'day')) {
    return withDateOf(withClampedYear, DEFAULT_MAX_DATE);
  }

  return withClampedYear;
};

const outOfRangeWarning = (value: dayjs.Dayjs) => {
  if (value.isBefore(DEFAULT_MIN_DATE, 'day')) {
    return ErrorMessages.DATE_BEFORE_MIN(MIN_DATE_LABEL);
  }
  if (value.isAfter(DEFAULT_MAX_DATE, 'day')) {
    return ErrorMessages.DATE_AFTER_MAX(MAX_DATE_LABEL);
  }

  return null;
};

type CombinedProps =
  | (MuiDateTimePickerProps<dayjs.Dayjs> & { date: true; time: true })
  | (DatePickerProps<dayjs.Dayjs> & { date: true; time?: false })
  | (TimePickerProps<dayjs.Dayjs> & { time: true; date?: false });

const DateTimePicker = forwardRef<HTMLDivElement, BaseProps & CombinedProps>(
  (props, ref) => {
    const {
      date = true,
      time = true,
      onChange,
      onError,
      onAccept,
      onBlur,
      textFieldProps,
      tooltip,
      error,
      warning,
      required,
      ['data-testid']: dataTestId,
      rootSx,
      loading,
      onClose,
      small,
      ignoreIncompleteEntries,
      value: externalValue,
      ...restProps
    } = props;
    const mergedTextFieldProps = {
      size: small ? 'small' : undefined,
      ...textFieldProps,
    } as TextFieldProps;
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(
      externalValue ?? null
    );

    const serialize = (v: dayjs.Dayjs | null | undefined) =>
      v && v.isValid() ? v.toISOString() : '';

    const lastEmittedRef = useRef<string>(serialize(externalValue));
    const [rangeWarning, setRangeWarning] = useState<string | null>(null);

    useEffect(() => {
      const incoming = serialize(externalValue);
      if (incoming === lastEmittedRef.current) return;
      setSelectedDate(externalValue ?? null);
      setRangeWarning(null);
      lastEmittedRef.current = incoming;
    }, [externalValue]);

    const pendingOutOfRangeRef = useRef<dayjs.Dayjs | null>(null);
    const hasUncommittedEntryRef = useRef(false);
    const [showIncompleteEntryError, setShowIncompleteEntryError] =
      useState(false);

    const handleChange = (
      value: dayjs.Dayjs | null,
      context?: { validationError?: unknown }
    ) => {
      const validationError = context?.validationError;
      const isOutOfRange =
        validationError === 'maxDate' || validationError === 'minDate';
      const isClampable =
        isOutOfRange &&
        !!value &&
        value.isValid() &&
        value.year() >= MIN_COMPLETE_YEAR;

      pendingOutOfRangeRef.current = isClampable ? value : null;

      const committedValue =
        value && value.isValid() && !validationError ? value : null;

      hasUncommittedEntryRef.current = !committedValue && value !== null;
      if (!hasUncommittedEntryRef.current) setShowIncompleteEntryError(false);
      setRangeWarning(null);

      if (hasUncommittedEntryRef.current && ignoreIncompleteEntries) return;

      lastEmittedRef.current = serialize(committedValue);
      if (committedValue) {
        setSelectedDate(committedValue);
      } else if (value === null) {
        setSelectedDate(null);
      }
      if (onChange) onChange(committedValue);
    };

    const settlePendingDateEdit = () => {
      const pending = pendingOutOfRangeRef.current;
      if (!pending) {
        setShowIncompleteEntryError(hasUncommittedEntryRef.current);
        return;
      }

      pendingOutOfRangeRef.current = null;
      hasUncommittedEntryRef.current = false;
      setShowIncompleteEntryError(false);

      const clampedValue = clampToRange(pending);
      setRangeWarning(outOfRangeWarning(pending));
      lastEmittedRef.current = serialize(clampedValue);
      setSelectedDate(clampedValue);
      if (onChange) onChange(clampedValue);
    };

    const handleFieldBlur = (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      settlePendingDateEdit();
      if (onBlur) onBlur(event);
    };

    const calendarJustClosed = useRef<boolean>(false);

    const formatTime = (time: string) => {
      let normalizedTime = time
        .trim()
        .replace(/([0-9])([aApP][mM])$/, '$1 $2')
        .toLowerCase();

      if (/^\d{2}(:\d{2})?$/.test(normalizedTime)) {
        return dayjs(normalizedTime, 'HH:mm');
      }

      if (/^\d{1,2}(:\d{2})?\s?[apmAPM]{2}$/.test(normalizedTime)) {
        normalizedTime = normalizedTime.toLowerCase();
        return dayjs(normalizedTime, ['h:mm a', 'h a'], true);
      }

      if (/^\d{1,2}$/.test(normalizedTime)) {
        return dayjs(normalizedTime, 'H');
      }

      return dayjs(time);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text');
      const parsedDate = dayjs(pastedText);

      if (parsedDate.isValid()) {
        setSelectedDate(parsedDate);
      }
    };

    const handlePasteTime = (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text');
      const parsedTime = formatTime(pastedText);

      if (parsedTime.isValid()) {
        setSelectedDate(parsedTime);
      }
    };

    const showRangeWarning =
      Boolean(rangeWarning) && !error && !showIncompleteEntryError;
    const feedbackProps = {
      error: error || showIncompleteEntryError,
      warning: warning || showRangeWarning,
      tooltip: showIncompleteEntryError ? (
        <TooltipErrors errors={[ErrorMessages.INVALID_DATE]}>
          <ErrorIcon />
        </TooltipErrors>
      ) : showRangeWarning ? (
        <TooltipErrors
          errors={[]}
          warnings={[rangeWarning as string]}
          type="warning"
        >
          <WarningIcon />
        </TooltipErrors>
      ) : (
        tooltip
      ),
    };

    const popperStyles = {
      disablePortal: props.disablePortal,
      sx: {
        '.MuiPickersCalendarHeader-root': {
          borderBottom: '1px solid #D9D9DE',
          marginBottom: '15px',
          minHeight: '56px',
          marginTop: '5px',
        },
        '.MuiDateCalendar-root': {
          width: '294px',
          minHeight: '350px',
        },
      },
    };

    if (loading) {
      return (
        <TextFieldLoader label={textFieldProps?.label} helperText={undefined} />
      );
    }

    if (date && time) {
      return (
        <Box
          sx={{
            ...datePickerStyles(mergedTextFieldProps.size || 'medium'),
            ...rootSx,
          }}
          data-testid={dataTestId}
        >
          <MuiDateTimePicker
            ref={ref}
            value={selectedDate}
            minDate={DEFAULT_MIN_DATE}
            maxDate={DEFAULT_MAX_DATE}
            slots={{
              textField: TextField,
              openPickerIcon: CalendarIcon,
              toolbar: () => null,
              actionBar: () => null,
            }}
            slotProps={{
              textField: {
                ...mergedTextFieldProps,
                onPaste: handlePaste,
                onBlur: handleFieldBlur,
                ...feedbackProps,
                required,
                autocompleteHtmlAttribute: 'off',
              } as TextFieldProps,
              inputAdornment: {
                position: 'end',
              },
              popper: popperStyles,
            }}
            onChange={handleChange}
            onError={(reason) => {
              if (onError) onError(reason);
            }}
            onAccept={(value) => {
              settlePendingDateEdit();
              if (onAccept) onAccept(value);
            }}
            {...(restProps as MuiDateTimePickerProps<dayjs.Dayjs>)}
          />
        </Box>
      );
    } else if (date) {
      return (
        <Box
          sx={{
            ...datePickerStyles(mergedTextFieldProps.size || 'medium'),
            ...rootSx,
          }}
          data-testid={dataTestId}
        >
          <DatePicker
            open={open}
            ref={ref}
            value={selectedDate}
            minDate={DEFAULT_MIN_DATE}
            maxDate={DEFAULT_MAX_DATE}
            sx={{
              '.MuiDialogActions-root': { backgroundColor: 'red !important' },
              '.MuiButtonBase-root': {
                display: 'none',
                backgroundColor: 'red !important',
              },
            }}
            onOpen={() => setOpen(true)}
            onClose={() => {
              setOpen(false);
              settlePendingDateEdit();
              onClose && onClose();
              calendarJustClosed.current = true;
              setTimeout(() => {
                calendarJustClosed.current = false;
              }, 200);
            }}
            onChange={handleChange}
            onAccept={(value) => {
              setOpen(false);
              settlePendingDateEdit();
              if (onAccept) onAccept(value);
            }}
            onError={(reason) => {
              if (onError) onError(reason);
            }}
            slots={{
              textField: TextField,
              openPickerIcon: () => null,
              toolbar: () => null,
              actionBar: () => null,
            }}
            slotProps={{
              textField: {
                ...mergedTextFieldProps,
                onPaste: handlePaste,
                onBlur: handleFieldBlur,
                ...feedbackProps,
                required,
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      if (calendarJustClosed.current === true) {
                        return;
                      }
                      setOpen(true);
                    }}
                  >
                    <CalendarIcon />
                  </IconButton>
                ),
                autocompleteHtmlAttribute: 'off',
              } as TextFieldProps,
              inputAdornment: {
                position: 'start',
              },
              popper: popperStyles,
            }}
            {...(restProps as DatePickerProps<dayjs.Dayjs>)}
          />
        </Box>
      );
    } else if (time) {
      return (
        <Box
          sx={{
            ...datePickerStyles(mergedTextFieldProps.size || 'medium'),
            ...rootSx,
          }}
          data-testid={dataTestId}
        >
          <TimePicker
            ref={ref}
            sx={datePickerStyles(mergedTextFieldProps.size || 'medium')}
            value={selectedDate}
            onChange={handleChange}
            onAccept={(value) => {
              if (onAccept) onAccept(value);
            }}
            onError={(reason) => {
              if (onError) onError(reason);
            }}
            slots={{
              textField: TextField,
              openPickerIcon: CalendarIcon,
              toolbar: () => null,
              actionBar: () => null,
            }}
            slotProps={{
              textField: {
                ...mergedTextFieldProps,
                onPaste: handlePasteTime,
                onBlur: onBlur,
                error,
                warning,
                tooltip,
                required,
                autocompleteHtmlAttribute: 'off',
              } as TextFieldProps,
              inputAdornment: {
                position: 'end',
              },
              popper: popperStyles,
            }}
            {...(restProps as TimePickerProps<dayjs.Dayjs>)}
          />
        </Box>
      );
    }

    return null;
  }
);

DateTimePicker.displayName = 'DateTimePicker';

export default DateTimePicker;
